import { z } from "zod";
import type { ProductFeatures } from "../deal.types";

const EMPTY_FEATURES: ProductFeatures = {
    width: null,
    height: null,
    outerColorId: null,
};

const productFeatureSchema = z.object({
    key: z.string(),
    value: z.unknown().optional(),
});

const numericFeatureValueSchema = z.unknown().transform((v) => {
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    if (typeof v === "string") {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }
    return null;
});

const stringFeatureValueSchema = z
    .unknown()
    .transform((v) => (typeof v === "string" ? v : null));

// Once the `{key, value}` items are folded into a `Record<string, unknown>`
// keyed by `key`, this is the actual feature shape we care about. Adding a
// new feature is one line here — no string literals scattered around.
const productFeaturesObjectSchema = z.object({
    width: numericFeatureValueSchema,
    height: numericFeatureValueSchema,
    outerColor: stringFeatureValueSchema,
});

/**
 * Parses the kozijn-CMS `features` blob (a JSON string that may contain
 * either an array of `{key, value}` items or a record of them keyed by
 * index) into a typed `ProductFeatures`. Always succeeds — invalid /
 * missing / malformed input resolves to an empty `ProductFeatures`.
 */
export const productFeaturesSchema = z
    .string()
    .nullish()
    .transform((s) => {
        if (!s) return null;
        try {
            return JSON.parse(s) as unknown;
        } catch {
            return null;
        }
    })
    .pipe(
        z.union([
            z.array(productFeatureSchema),
            z
                .record(z.string(), productFeatureSchema)
                .transform((rec) => Object.values(rec)),
            z.null(),
        ]),
    )
    .transform((items) =>
        items ? Object.fromEntries(items.map((f) => [f.key, f.value])) : {},
    )
    .pipe(productFeaturesObjectSchema)
    .transform(
        (parsed): ProductFeatures => ({
            width: parsed.width,
            height: parsed.height,
            outerColorId: parsed.outerColor,
        }),
    )
    .catch(EMPTY_FEATURES);
